import React, { useContext, useMemo, useState } from 'react';
import PanelHeader from '../../components/panelheader/panelHeader';
import { useIntl } from 'react-intl';
import { useLoader } from '../../api/useLoader';
import { AdminApiContext } from '../../api/AdminApiContext';
import Spinner from '../../components/spinner/spinner';
import { PraksisstedMedInfoDto } from '../../api/generated/admin-generated-api';
import { EmptyState } from '../../components/emptyState/emptyState';
import translations from './praksisstedsok.i18n';
import { CustomNodeElementProps } from 'react-d3-tree/lib/types/types/common';
import Tree from 'react-d3-tree';
import * as css from './praksisstedsok.module.scss';
import useElementBoundingClientRect from '../../hooks/useElementBoundingClientRect';
import { overflowEllipsis } from '../../util/stringutils';
import {
  D3_RENDER_VALUES,
  D3TreeProps,
  MAX_CHARS_NODE_NAME,
  NodeCircleProps,
  NodeCircleTypes,
  NodeProps,
  PraksisstedTreeLoaderProps,
  PraksisstedTreeProps,
} from './types';
import {
  flattenUndernoder,
  getNodeCircleClassName,
  getNodeCircleType,
  hasAnyRelevantChild,
  hasAnyRelevantChildFlattenedUndernoder,
  mapPraksisstedToRawNodeDatum,
} from './functions';
import PraksisstedInfoModal from './praksisstedinfomodal';

const PraksisstedTreeLoader = React.memo<PraksisstedTreeLoaderProps>(
  ({ chosenPraksisstedId, includeDeleted }) => {
    const { praksisstedApi } = useContext(AdminApiContext);

    const loaderParams = useMemo(
      () => ({
        pathParams: {
          id: chosenPraksisstedId,
        },
        queryParams: {
          includeDeleted,
        },
      }),
      [chosenPraksisstedId, includeDeleted],
    );

    const { data, loading } = useLoader(
      () => praksisstedApi.praksisstedstreIdGet(loaderParams),
      [loaderParams],
    );

    const { formatMessage } = useIntl();
    const labels = useMemo(
      () => ({
        praksisstedTreeEmptyTitle: formatMessage({
          id: translations.praksisstedTreeEmptyTitle.id,
        }),
        praksisstedTreeEmptyText: formatMessage({
          id: translations.praksisstedTreeEmptyText.id,
        }),
      }),
      [formatMessage],
    );

    if (loading) return <Spinner height={'3em'} />;

    return data ? (
      <PraksisstedTree praksissted={data} />
    ) : (
      <EmptyState
        title={labels.praksisstedTreeEmptyTitle}
        text={labels.praksisstedTreeEmptyText}
      />
    );
  },
);

const PraksisstedTree: React.FC<
  React.PropsWithChildren<PraksisstedTreeProps>
> = ({ praksissted }) => {
  const { selv, overnoder, undernoder } = praksissted;
  const { navn } = selv;

  const [praksisstedInfoModalId, setPraksisstedInfoModalId] =
    useState<string>();
  const [containerRect, containerRef] =
    useElementBoundingClientRect<HTMLDivElement>();

  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      praksisstedTreeTitle: formatMessage(
        { id: translations.praksisstedTreeTitle.id },
        { navn },
      ),
      praksisstedTreeSubtitle: formatMessage(
        { id: translations.praksisstedTreeSubtitle.id },
        { navn },
      ),
    }),
    [formatMessage, navn],
  );

  const onPraksisstedInfoModalClose = () =>
    setPraksisstedInfoModalId(undefined);

  const chosenPraksisstedForModal = useMemo<
    PraksisstedMedInfoDto | undefined
  >(() => {
    if (praksisstedInfoModalId) {
      if (praksisstedInfoModalId === selv.id) return selv;

      return [...overnoder, ...flattenUndernoder(undernoder)].find(
        (sted) => sted.id === praksisstedInfoModalId,
      );
    }
  }, [selv, undernoder, overnoder, praksisstedInfoModalId]);

  const flattenedTree = useMemo(
    () => [...overnoder, selv, ...flattenUndernoder(undernoder)],
    [overnoder, selv, undernoder],
  );

  const chosenIdIsOvernode = useMemo(
    () =>
      praksisstedInfoModalId
        ? overnoder
            .map((overnode) => overnode.id)
            .includes(praksisstedInfoModalId)
        : false,
    [overnoder, praksisstedInfoModalId],
  );

  const flattenedUndernoder = useMemo(
    () =>
      chosenPraksisstedForModal
        ? selv.id === praksisstedInfoModalId || chosenIdIsOvernode
          ? flattenUndernoder(undernoder)
          : flattenUndernoder(chosenPraksisstedForModal.undernoder)
        : [],
    [
      praksisstedInfoModalId,
      chosenPraksisstedForModal,
      selv,
      undernoder,
      chosenIdIsOvernode,
    ],
  );

  return (
    <PanelHeader
      title={labels.praksisstedTreeTitle}
      subtitle={labels.praksisstedTreeSubtitle}
      collapsible
    >
      <DescriptionIcons />
      <div className={css.treeContainer} ref={containerRef}>
        {containerRect !== undefined && (
          <D3Tree
            praksissted={praksissted}
            containerWidth={containerRect.width}
            onNodeClick={(node) => {
              const { attributes } = node.data;

              if (attributes !== undefined && attributes.hasOwnProperty('id')) {
                setPraksisstedInfoModalId(attributes.id as string);
              }
            }}
          />
        )}
      </div>
      {praksisstedInfoModalId && chosenPraksisstedForModal && (
        <PraksisstedInfoModal
          onClose={onPraksisstedInfoModalClose}
          praksissted={chosenPraksisstedForModal}
          flattenedUndernoder={flattenedUndernoder}
          flattenedTree={flattenedTree}
          showOvernodeWarning={chosenIdIsOvernode}
        />
      )}
    </PanelHeader>
  );
};

const NodeCircle: React.FC<React.PropsWithChildren<NodeCircleProps>> = ({
  nodeCircleType,
  cx,
  cy,
}) => (
  <circle
    r={10}
    cx={cx}
    cy={cy}
    className={getNodeCircleClassName(nodeCircleType)}
  />
);

const DescriptionIcons = () => {
  const { formatMessage } = useIntl();
  const labels = useMemo<{ [key in NodeCircleTypes]: string }>(
    () => ({
      [NodeCircleTypes.MainRelevant]: formatMessage({
        id: translations.praksisstedTreeNodeMainRelevant.id,
      }),
      [NodeCircleTypes.MainIrrelevant]: formatMessage({
        id: translations.praksisstedTreeNodeMainIrrelevant.id,
      }),
      [NodeCircleTypes.Relevant]: formatMessage({
        id: translations.praksisstedTreeNodeRelevant.id,
      }),
      [NodeCircleTypes.Irrelevant]: formatMessage({
        id: translations.praksisstedTreeNodeIrrelevant.id,
      }),
      [NodeCircleTypes.Overnode]: formatMessage({
        id: translations.praksisstedTreeOvernode.id,
      }),
    }),
    [formatMessage],
  );

  return (
    <div className={css.descriptionIcons}>
      {Object.values(NodeCircleTypes).map((nodeCircleType) => (
        <div className={css.descriptionIcon} key={nodeCircleType}>
          <svg
            viewBox="0 0 40 40"
            xmlns="http://www.w3.org/2000/svg"
            version="1.1"
          >
            <NodeCircle nodeCircleType={nodeCircleType} cx={20} cy={20} />
          </svg>
          <p>{labels[nodeCircleType]}</p>
        </div>
      ))}
    </div>
  );
};

const Node: React.FC<React.PropsWithChildren<NodeProps>> = ({
  isLeafNode,
  nodeCircleType,
  name,
  onClick,
}) => (
  <g onClick={onClick}>
    <NodeCircle nodeCircleType={nodeCircleType} />
    <text
      x={isLeafNode ? -30 : 15}
      y={isLeafNode ? 25 : 5}
      className={css.name}
    >
      {isLeafNode ? overflowEllipsis(name, MAX_CHARS_NODE_NAME) : name}
    </text>
  </g>
);

const renderNode = (
  nodeProps: CustomNodeElementProps,
  overnoder: PraksisstedMedInfoDto[],
  flattenedUndernoder: PraksisstedMedInfoDto[],
  praksissted: PraksisstedMedInfoDto,
): React.ReactElement => {
  const { nodeDatum, onNodeClick } = nodeProps;
  const { name, attributes, children } = nodeDatum;

  if (!attributes || !attributes.hasOwnProperty('id')) return <></>;

  const isMainNode = praksissted.id === attributes.id;

  const currentPraksissted = isMainNode
    ? praksissted
    : [...overnoder, ...flattenedUndernoder].find(
        (node) => node.id === attributes.id,
      )!;

  const { relevant } = currentPraksissted;

  const isRelevantOrHasRelevantChild =
    relevant ||
    (isMainNode
      ? hasAnyRelevantChildFlattenedUndernoder(flattenedUndernoder)
      : hasAnyRelevantChild(currentPraksissted));

  const nodeCircleType = getNodeCircleType(
    overnoder.includes(currentPraksissted),
    isMainNode,
    isRelevantOrHasRelevantChild,
  );

  const isLeafNode = (children?.length || 0) === 0;

  return (
    <Node
      isLeafNode={isLeafNode}
      nodeCircleType={nodeCircleType}
      name={name}
      onClick={onNodeClick}
    />
  );
};

const D3Tree: React.FC<React.PropsWithChildren<D3TreeProps>> = ({
  praksissted,
  containerWidth,
  onNodeClick,
}) => {
  const {
    ORIENTATION,
    TRANSLATE_X_DIVISOR,
    TRANSLATE_Y,
    DEPTH_FACTOR,
    SEPARATION,
    NODE_SIZE,
  } = D3_RENDER_VALUES;

  const data = useMemo(
    () => mapPraksisstedToRawNodeDatum(praksissted),
    [praksissted],
  );

  const flattenedUndernoder = useMemo(
    () => flattenUndernoder(praksissted.undernoder),
    [praksissted.undernoder],
  );

  return (
    <Tree
      data={data}
      orientation={ORIENTATION}
      translate={{ x: containerWidth / TRANSLATE_X_DIVISOR, y: TRANSLATE_Y }}
      separation={SEPARATION}
      nodeSize={NODE_SIZE}
      depthFactor={DEPTH_FACTOR}
      onNodeClick={onNodeClick}
      renderCustomNodeElement={(nodeProps) =>
        renderNode(
          nodeProps,
          praksissted.overnoder,
          flattenedUndernoder,
          praksissted.selv,
        )
      }
    />
  );
};

export default PraksisstedTreeLoader;
